import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { assessmentExchangeApi } from '../../services/assessmentExchangeApi';

export const assessmentExchangeKeys = {
  all: ['assessmentExchanges'] as const,
  lists: () => [...assessmentExchangeKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; search?: string }) =>
    [...assessmentExchangeKeys.lists(), filters] as const,
  byClinicalSession: (clinicalSessionId: string) =>
    [...assessmentExchangeKeys.all, 'byClinicalSession', clinicalSessionId] as const,
  details: () => [...assessmentExchangeKeys.all, 'detail'] as const,
  detail: (id: string) => [...assessmentExchangeKeys.details(), id] as const,
};

export function useGetAssessmentExchange(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assessmentExchangeKeys.detail(id),
    queryFn: async () => {
      const response = await assessmentExchangeApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment exchange'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetAssessmentExchanges() {
  return useQuery({
    queryKey: assessmentExchangeKeys.lists(),
    queryFn: async () => {
      const response = await assessmentExchangeApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment exchanges'}`);
      }
      return response;
    },
  });
}

export function useGetAssessmentExchangesByClinicalSessionId(
  clinicalSessionId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: assessmentExchangeKeys.byClinicalSession(clinicalSessionId),
    queryFn: async () => {
      const response = await assessmentExchangeApi.getByClinicalSessionId(clinicalSessionId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment exchanges'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!clinicalSessionId,
  });
}

export function useGetAssessmentExchangesPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery({
    queryKey: assessmentExchangeKeys.list({ pageNumber, pageSize, search }),
    queryFn: async () => {
      const response = await assessmentExchangeApi.getPagedList(pageNumber, pageSize, search);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment exchanges'}`);
      }
      return response;
    },
  });
}
